// MARK://dhk8/realtime/use-realtime-subscription
// React Context + hooks over WsClient. Port of sc-restaurant's useRealtime.tsx,
// generalised: the provider owns one WsClient; consumers subscribe to channels
// with a stable-by-ref handler so the WS subscription only re-runs when the
// channel (or explicit deps) change.
import { createContext, createElement, useContext, useEffect, useRef, useState, } from 'react';
import { WsClient } from './ws-client.js';
const RealtimeContext = createContext({
    client: null,
    status: 'idle',
    userId: null,
    tenantId: null,
    branchIds: [],
});
export function RealtimeProvider({ url, token, autoReconnect = true, heartbeatMs, onReconnect, enabled = true, children, }) {
    const [client, setClient] = useState(null);
    const [status, setStatus] = useState('idle');
    const [userId, setUserId] = useState(null);
    const [tenantId, setTenantId] = useState(null);
    const [branchIds, setBranchIds] = useState([]);
    const onReconnectRef = useRef(onReconnect);
    useEffect(() => { onReconnectRef.current = onReconnect; }, [onReconnect]);
    useEffect(() => {
        if (!enabled) {
            setClient(null);
            setStatus('idle');
            return;
        }
        const c = new WsClient({ url, token, autoReconnect, ...(heartbeatMs !== undefined ? { heartbeatMs } : {}) });
        setClient(c);
        setStatus('connecting');
        const offOpen = c.on('open', (info) => {
            setStatus('open');
            setUserId(c.userId);
            setTenantId(c.tenantId);
            setBranchIds([...c.branchIds]);
            const reconnected = !!(info && typeof info === 'object' && 'reconnected' in info && info.reconnected);
            if (reconnected) {
                try {
                    onReconnectRef.current?.();
                }
                catch { /* noop */ }
                if (typeof window !== 'undefined') {
                    try {
                        window.dispatchEvent(new Event('dhk8:rehydrate'));
                    }
                    catch { /* noop */ }
                }
            }
        });
        const offClose = c.on('close', () => setStatus('closed'));
        void c.connect().catch(() => setStatus('closed'));
        return () => {
            offOpen();
            offClose();
            c.close();
        };
    }, [enabled, url, token, autoReconnect, heartbeatMs]);
    return createElement(RealtimeContext.Provider, { value: { client, status, userId, tenantId, branchIds } }, children);
}
/**
 * Subscribe to a realtime channel. The handler is stored in a ref, so the
 * underlying WS subscription is only re-created when `channel` or `deps` change.
 */
export function useRealtimeSubscription(channel, handler, deps = []) {
    const { client } = useContext(RealtimeContext);
    const handlerRef = useRef(handler);
    useEffect(() => { handlerRef.current = handler; }, [handler]);
    useEffect(() => {
        if (!client)
            return;
        const unsubscribe = client.subscribe(channel, (envelope) => {
            handlerRef.current(envelope);
        });
        return () => { unsubscribe(); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [client, channel, ...deps]);
}
export function useRealtimeStatus() {
    return useContext(RealtimeContext).status;
}
export function useRealtimeContext() {
    return useContext(RealtimeContext);
}
//# sourceMappingURL=use-realtime-subscription.js.map